import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/styles";
import { FormControlLabel, Checkbox, Snackbar, Divider } from "@material-ui/core";
import { ReflexContainer, ReflexSplitter, ReflexElement } from "react-reflex";
import { Alert } from "@material-ui/lab";
import { debounce } from "lodash";

import uiConf from "../../config/uiConf";

import apiClient from "../../lib/common/apiClient";
import checkValidation from "../../lib/utils/checkValidation";
import checkConditionWithMsg from "../../lib/utils/checkConditionWithMsg";

import Dialog from "../dialog/functional/Dialog";
import DialogHeader from "../dialog/functional/DialogHeader";
import DialogContent from "../dialog/functional/DialogContent";
import DialogTitle from "../dialog/functional/DialogTitle";
import DialogActionsOkCancel from "../dialog/functional/DialogActionsOkCancel";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import AutoForm from "../form/functional/AutoForm";
import layoutConf from "../../config/layoutConf";
import failureKey from "../../lib/common/failureKey";

const useStyles = makeStyles((theme) => ({
    keepOpenSwitch: {
        marginLeft: "8px",
    },

    label: {
        fontSize: "smaller",
        color: theme.palette.primary.text,
    },
}));

function MomSysBackShipDetailDialog(props) {
    const classes = useStyles();
    const { t } = useTranslation();

    const {
        id,
        schema,

        moveable,
        resizable,
        fullWidth,
        maxWidth,

        onClose,
        onInitialized,
        onSuccess,

        title,
        formId,
        refresh,

        onSelectMst,

        customizeErrorMsgCallback,

        ...others
    } = props;

    const [dialogInstance, setDialogInstance] = useState();
    const [okButtonDisabled, setOkButtonDisabled] = useState(true);
    const [formData, setFormData] = useState({});
    const [keepOpen, setKeepOpen] = useState(false);

    const [deliveryMaterialGrid, setDeliveryMaterialGrid] = useState();
    const [deliveryMaterialSelect, setDeliveryMaterialSelect] = useState();

    const [deliveryMaterialLotGrid, setDeliveryMaterialLotGrid] = useState();
    const [deliveryMaterialLotApi, setDeliveryMaterialLotApi] = useState();
    const [deliveryMaterialLotSelect, setDeliveryMaterialLotSelect] = useState();

    const [notification, setNotification] = useState({
        open: false,
        severity: "success",
        msg: "",
    });

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const nofifyWarning = (msg) => {
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onNotifySuccess = (msg) => {
        notifySuccess(msg);

        if (refresh) {
            refresh.refresh();
        }
    };

    useEffect(() => {
        (async () => {
            if (deliveryMaterialGrid) {
                deliveryMaterialGrid.selectionChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setDeliveryMaterialSelect(grid.selectedItems[0]);
                        } else {
                            setDeliveryMaterialSelect();
                        }
                    },10)
                );

                deliveryMaterialGrid.itemsSourceChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setDeliveryMaterialSelect(grid.selectedItems[0]);
                        } else {
                            setDeliveryMaterialSelect();
                        }
                    },10)
                );
            }
        })();
    }, [deliveryMaterialGrid]);

    useEffect(() => {
        (async () => {
            if (deliveryMaterialLotGrid) {
                deliveryMaterialLotGrid.selectionChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setDeliveryMaterialLotSelect(grid.selectedItems[0]);
                        } else {
                            setDeliveryMaterialLotSelect();
                        }
                    },10)
                );

                deliveryMaterialLotGrid.itemsSourceChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setDeliveryMaterialLotSelect(grid.selectedItems[0]);
                        } else {
                            setDeliveryMaterialLotSelect();
                        }
                    },10)
                );
            }
        })();
    }, [deliveryMaterialLotGrid]);

    useEffect(() => {
        if (deliveryMaterialSelect && deliveryMaterialLotSelect) {
            setFormData((formData) => {
                let newFormData = {
                    ...formData,

                    shipDetailId: deliveryMaterialSelect.shipDetailId,
                    orderBuyerDetailId: deliveryMaterialSelect.orderBuyerDetailId,

                    materialCode: deliveryMaterialSelect.materialCode,
                    materialCodeVer: deliveryMaterialSelect.materialCodeVer,
                    materialName: deliveryMaterialSelect.materialName,
                    itemUnitName: deliveryMaterialSelect.itemUnitName,
                    itemSpecName: deliveryMaterialSelect.itemSpecName,
                    shippedLotNo: deliveryMaterialLotSelect.lotNo,

                    shipQty: deliveryMaterialLotSelect.shipQty,
                    shipMediumUnitQty: deliveryMaterialLotSelect.shipMediumUnitQty,
                    shipSmallUnitQty: deliveryMaterialLotSelect.shipSmallUnitQty,

                    deliveryQty: deliveryMaterialSelect.deliveryQty,
                    deliveryMediumUnitQty: deliveryMaterialSelect.deliveryMediumUnitQty,
                    deliverySmallUnitQty: deliveryMaterialSelect.deliverySmallUnitQty,

                };

                return newFormData;
            });
        }
    }, [deliveryMaterialSelect, deliveryMaterialSelect?.materialCode, deliveryMaterialSelect?.materialCodeVer, deliveryMaterialLotSelect]);

    useEffect(() => {
        if (deliveryMaterialLotApi) {
            deliveryMaterialLotApi.refresh();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deliveryMaterialSelect]);

    const onDialogInitialized = (instance) => {
        setDialogInstance(instance);

        if (onInitialized) {
            onInitialized(instance);
        }
    };

    const onApiSuccess = (result) => {
        setKeepOpen((keepOpen) => {
            if (!keepOpen) {
                dialogInstance.hide();
            }

            if (onSuccess) {
                setTimeout(() => onSuccess(result.data), 0);
            } else {
                onNotifySuccess(result.data);
            }

            return keepOpen;
        });
    };

    const onApiFailure = (reason) => {
        if (customizeErrorMsgCallback) {
            notifyFailure(customizeErrorMsgCallback(reason));
        } else {
            notifyFailure(
                t(failureKey("insert", reason.response?.status), { reason: reason.response ? reason.response.data : JSON.stringify(reason) })
            );
        }
    };

    const checkItemValidataion = (schema, formData) => {
        for (const column of schema.columns) {
            let [valid, msg] = checkValidation("create", column, column.validation, formData[column.name] || "");

            if (valid && column.name === "qtyBackShip" && deliveryMaterialLotSelect) {
                if (
                    Number(formData["qtyBackShip"]) + deliveryMaterialLotSelect.backShipQty >
                    deliveryMaterialLotSelect.shipQty
                ) {
                    valid = false;
                    msg = t("error.failToQtyBackShipIsBiggerThanQtyShip");
                }
            }

            if (!valid) {
                nofifyWarning(msg);
                return false;
            }
        }

        return true;
    };

    const onOk = (schema, formData) => {
        if (!checkItemValidataion(schema, formData)) {
            console.error("Form Validation Failed");

            return;
        }

        if (schema.validation) {
            let [valid, msg] = checkConditionWithMsg(formData, schema.validation);

            if (!valid) {
                nofifyWarning(t("warning.formDataValidationFail", { msg: msg }));
                return;
            }
        }

        // build base api url

        let url = `/api/${schema.name}/${onSelectMst.backShipId}`;

        let apiData = { ...formData };

        let form = new FormData();

        let json = {};
        let deleted = {};

        for (const formItem in apiData) {
            if (schema.columnMap && schema.columnMap[formItem] && schema.columnMap[formItem].formItemType === "file") {
                deleted[formItem] = apiData[formItem].deletedFiles;

                for (const file of apiData[formItem].activeFiles) {
                    if (file.type === "url") continue;

                    form.append(formItem, file.file);
                }
            } else {
                if (apiData[formItem] !== undefined) {
                    json[formItem] = apiData[formItem];
                }
            }
        }

        form.append("deleted", JSON.stringify(deleted));
        form.append("json", JSON.stringify(json));

        apiClient
            .post(url, form, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            .then((result) => onApiSuccess(result))
            .catch((reason) => onApiFailure(reason));
    };

    const onCancel = () => {
        dialogInstance.hide();
    };

    const checkKeepOpen = () => {
        return (
            <>
                <FormControlLabel
                    control={
                        <Checkbox
                            className={classes.keepOpenSwitch}
                            checked={keepOpen}
                            color="primary"
                            onChange={() => setKeepOpen((keepOpen) => !keepOpen)}
                            name="checkKeepOpen"
                            size="small"
                        />
                    }
                    label={<span className={classes.label}>{t("term.keepDialogOpen")}</span>}
                />
                <div style={{ flexGrow: 1 }}></div>
            </>
        );
    };

    const onChangeFormData = (formData) => {
        setFormData(formData);

        setOkButtonDisabled(false);
    };

    const onCloseNotification = () => {
        setNotification((notification) => {
            return { ...notification, open: false };
        });
    };

    const onCloseDialog = () => {
        if (onClose) {
            onClose();
        }

        setFormData();
    };

    return (
        <Dialog
            id={id}
            moveable={moveable ? moveable : uiConf.dialogMoveable}
            resizable={resizable ? resizable : uiConf.dialogResizable}
            fullWidth={fullWidth ? fullWidth : uiConf.dialogFullWidth}
            maxWidth="lg"
            onClose={onCloseDialog}
            onInitialized={onDialogInitialized}
            {...others}
        >
            <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
            </DialogHeader>

            <Divider />

            <DialogContent style={{ padding: "0px", height: "600px" }}>
                <ReflexContainer orientation="vertical">
                    <ReflexElement flex={0.65}>
                        <ReflexContainer orientation="horizontal">
                            <ReflexElement flex={0.5}>
                                <BasicCrudScreenType
                                    id={formId}
                                    embededInOtherScreenType
                                    headerTitle={t("tableName.MomSysDeliveryDetail")}
                                    view="MomSysVwDeliveryMaterialList"
                                    noCreate
                                    noEdit
                                    noDelete
                                    noExcel
                                    noFilter
                                    onInitialized={(grid) => {
                                        grid.selectionMode = "Row";
                                        setDeliveryMaterialGrid(grid);
                                    }}
                                    onModifyViewUrl={(url) => `${url}/${onSelectMst.shipId}`}
                                />
                            </ReflexElement>
                            <ReflexSplitter style={{ height: layoutConf.reflex.splitterHeight }} />
                            <ReflexElement flex={0.5}>
                                {deliveryMaterialSelect ? (
                                    <BasicCrudScreenType
                                        id={formId}
                                        embededInOtherScreenType
                                        headerTitle={t("tableName.MomSysDeliveryDetailLot")}
                                        view="MomSysVwDeliveryMaterialLotList"
                                        noCreate
                                        noEdit
                                        noDelete
                                        noExcel
                                        noFilter
                                        onInitialized={(grid) => {
                                            grid.selectionMode = "Row";
                                            setDeliveryMaterialLotGrid(grid);
                                        }}
                                        onApiInitialized={(api) => {
                                            setDeliveryMaterialLotApi(api);
                                        }}
                                        onModifyViewUrl={(url) => `${url}/${deliveryMaterialSelect.shipDetailId}`}
                                    />
                                ) : (
                                    <></>
                                )}
                            </ReflexElement>
                        </ReflexContainer>
                    </ReflexElement>

                    <ReflexSplitter style={{ width: layoutConf.reflex.splitterWidth }} />

                    <ReflexElement flex={0.35}>
                        <div
                            style={{
                                padding: "16px",
                            }}
                        >
                            <AutoForm
                                id="backship-detail-create-form"
                                schema={schema}
                                mode="edit"
                                initialData={formData}
                                onChange={onChangeFormData}
                            />
                        </div>
                    </ReflexElement>
                </ReflexContainer>

                <Snackbar
                    open={notification.open && notification.severity !== "success"}
                    autoHideDuration={uiConf.dialogNotifyAutoHideDuration}
                    onClose={onCloseNotification}
                >
                    <Alert onClose={onCloseNotification} variant="filled" severity={notification.severity}>
                        {notification.msg}
                    </Alert>
                </Snackbar>
            </DialogContent>

            <Divider />

            <DialogActionsOkCancel
                firstActionComponent={checkKeepOpen}
                labels={[t("common.add")]}
                buttonDisabled={[okButtonDisabled, false]} /* [okButtonDisabled, cancelButtonDisabled] */
                onOk={() => onOk(schema, formData)}
                onCancel={onCancel}
            />
        </Dialog>
    );
}

export default MomSysBackShipDetailDialog;
